import React, { ReactNode, createContext, useContext, FC, useState, useMemo, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { useSelector } from 'react-redux';

import {
  ICreateNotificationRequestDTO,
  INotificationMessageDTO,
  INotificationUserEmployeeDTO,
  IUserInfoDTO,
} from 'src/api/dtos';
import { NotificationMessageTypeEnums } from 'src/features/message-center/MessageCenter.enums';
import { formatUserEmployeeName } from 'src/features/message-center/MessageCenter.utils';
import { Modal } from 'src/shared/components';
import { MESSAGE_CENTER_DATE_FORMAT } from 'src/shared/constants';
import { useTranslate } from 'src/shared/providers';
import { RootState } from 'src/store/types';

const NEW_MESSAGE_PARAMS: ICreateNotificationRequestDTO = {
  subject: '',
  message: '',
  userIds: [],
  excludeUserIds: [],
  departmentIds: [],
  teamIds: [],
};

type MessageCenterEditProps = {
  inputs: ICreateNotificationRequestDTO | undefined;
  type: NotificationMessageTypeEnums | undefined;
  isEdited: boolean;
  onUserIdsChange: (userIds: number[]) => void;
  onExcludeUserIdsChange: (excludeUserIds: number[]) => void;
  onDepartmentIdsChange: (userIds: number[]) => void;
  onTeamIdsChange: (userIds: number[]) => void;
  onSubjectChange: (subject: string) => void;
  onMessageChange: (subject: string) => void;
  onNewMessage: () => void;
  onEditMessage: (notification: INotificationMessageDTO, type: NotificationMessageTypeEnums) => void;
  onCancel: (force?: boolean) => void;
};

const MessageCenterEditContext = createContext<MessageCenterEditProps>({} as MessageCenterEditProps);

export const useMessageCenterEdit = (): MessageCenterEditProps =>
  useContext<MessageCenterEditProps>(MessageCenterEditContext);

export const MessageCenterEditProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslate();
  const { info } = useSelector((state: RootState) => state.user);

  const [initialInputs, setInitialInputs] = useState<ICreateNotificationRequestDTO>();
  const [inputs, setInputs] = useState<ICreateNotificationRequestDTO>();
  const [type, setType] = useState<NotificationMessageTypeEnums>();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const isEdited = useMemo(() => !isEqual(initialInputs, inputs), [initialInputs, inputs]);

  // Reset inputs on initial inputs change
  useEffect(() => setInputs(initialInputs), [initialInputs]);

  const handleNewMessage = () => {
    setInitialInputs(NEW_MESSAGE_PARAMS);
    setType(NotificationMessageTypeEnums.NEW);
  };

  const handleEditMessage = (notification: INotificationMessageDTO, type: NotificationMessageTypeEnums) => {
    const editMessage: ICreateNotificationRequestDTO = {
      subject: getSubject(notification.subject, type),
      message: getInitialMessage(notification),
      userIds: getInitialUserIds(notification.fromUser, type, info),
      excludeUserIds: getInitialExcludeUserIds(info, type),
      departmentIds: [],
      teamIds: [],
      replyToNotificationId:
        type === NotificationMessageTypeEnums.REPLY_ALL || type === NotificationMessageTypeEnums.REPLY
          ? notification.id
          : undefined,
      notificationId: type === NotificationMessageTypeEnums.REPLY_ALL ? notification.id : undefined,
    };
    setInitialInputs(editMessage);
    setType(type);
  };

  const handleUserIdsChange = (userIds: number[]) => {
    setInputs((prevState) => {
      if (!prevState) {
        return;
      }

      return { ...prevState, userIds };
    });
  };

  const handleExcludeUserIdsChange = (excludeUserIds: number[]) => {
    setInputs((prevState) => {
      if (!prevState) {
        return;
      }

      return { ...prevState, excludeUserIds };
    });
  };

  const handleDepartmentIdsChange = (departmentIds: number[]) => {
    setInputs((prevState) => {
      if (!prevState) {
        return;
      }

      return { ...prevState, departmentIds };
    });
  };

  const handleTeamIdsChange = (teamIds: number[]) => {
    setInputs((prevState) => {
      if (!prevState) {
        return;
      }

      return { ...prevState, teamIds };
    });
  };

  const handleSubjectChange = (subject: string) => {
    setInputs((prevState) => {
      if (!prevState) {
        return;
      }

      return { ...prevState, subject };
    });
  };

  const handleMessageChange = (message: string) => {
    setInputs((prevState) => {
      if (!prevState) {
        return;
      }

      return { ...prevState, message };
    });
  };

  const handleCancel = (force?: boolean) => {
    if (inputs && isEdited && !force) {
      openCancelModal();
    } else {
      setInitialInputs(undefined);
    }
  };

  const openCancelModal = () => {
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
  };

  const submitCancelModal = () => {
    closeCancelModal();
    setInitialInputs(undefined);
  };

  return (
    <MessageCenterEditContext.Provider
      value={{
        inputs,
        type,
        isEdited,
        onUserIdsChange: handleUserIdsChange,
        onExcludeUserIdsChange: handleExcludeUserIdsChange,
        onDepartmentIdsChange: handleDepartmentIdsChange,
        onTeamIdsChange: handleTeamIdsChange,
        onSubjectChange: handleSubjectChange,
        onMessageChange: handleMessageChange,
        onNewMessage: handleNewMessage,
        onEditMessage: handleEditMessage,
        onCancel: handleCancel,
      }}
    >
      {children}
      <Modal
        open={cancelModalOpen}
        onClose={closeCancelModal}
        modalHeaderProps={{ title: t('messageCenter.cancelSendingMessage'), onClose: closeCancelModal }}
        modalFooterProps={{
          submitText: t('global.continue'),
          onSubmit: submitCancelModal,
          closeText: t('global.cancel'),
          onClose: closeCancelModal,
        }}
      >
        <Stack direction="column" gap={2}>
          <Typography>{t('messageCenter.youAreCancelingAMessage')}</Typography>
          <Typography>{t('messageCenter.areYouSureYouWantToCancelTheSendingOf')}</Typography>
        </Stack>
      </Modal>
    </MessageCenterEditContext.Provider>
  );
};

const getSubject = (subject: string, type: NotificationMessageTypeEnums) => {
  if (type === NotificationMessageTypeEnums.FORWARD && !subject.startsWith('Fwd:')) {
    return `Fwd: ${subject}`;
  }

  if (
    type === NotificationMessageTypeEnums.REPLY ||
    (type === NotificationMessageTypeEnums.REPLY_ALL && !subject.startsWith('Re:'))
  ) {
    return `Re: ${subject}`;
  }

  return subject;
};

const getInitialUserIds = (
  fromUser: INotificationUserEmployeeDTO,
  type: NotificationMessageTypeEnums,
  info: IUserInfoDTO | null,
): number[] => {
  if (!info) {
    return [];
  }

  // Don't include "from" user from recipients if it's the current user for "reply all"
  if (type === NotificationMessageTypeEnums.REPLY_ALL) {
    return fromUser.id === info.id ? [] : [fromUser.id];
  }

  // Always include "from" user for "reply"
  if (type === NotificationMessageTypeEnums.REPLY) {
    return [fromUser.id];
  }

  return [];
};

const getInitialExcludeUserIds = (info: IUserInfoDTO | null, type: NotificationMessageTypeEnums): number[] => {
  if (!info) {
    return [];
  }

  // Exclude current user for "reply all"
  if (type === NotificationMessageTypeEnums.REPLY_ALL) {
    return [info.id];
  }

  return [];
};

const getInitialMessage = (notification: INotificationMessageDTO) => {
  return `\n\n\n----------------------------------------------
  Date: ${moment(notification.date).format(MESSAGE_CENTER_DATE_FORMAT)}
  From: ${formatUserEmployeeName(notification.fromUser)}
  Subject: ${notification.subject}\n
  ${notification.message ?? ''}
  `;
};
